import React, { useState, useEffect } from 'react';
import { ref, get, set, update, remove, push } from 'firebase/database';
import { database } from '../../services/firebase';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/common/Header';

export default function AdminAnnouncements() {
    const { userRole } = useAuth();
    const [announcements, setAnnouncements] = useState([]);
    const [filteredAnnouncements, setFilteredAnnouncements] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');

    // États pour les filtres
    const [searchQuery, setSearchQuery] = useState('');
    const [importanceFilter, setImportanceFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [audienceFilter, setAudienceFilter] = useState('all');

    // États pour le formulaire
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [formMode, setFormMode] = useState('create'); // 'create' ou 'edit'
    const [currentAnnouncement, setCurrentAnnouncement] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        content: '',
        importance: 'normal',
        published: false,
        expiresAt: '',
        targetAudience: 'all'
    });

    // Récupération des annonces depuis Firebase
    useEffect(() => {
        async function fetchData() {
            try {
                if (userRole !== 'admin') {
                    setError('Accès non autorisé. Vous devez être administrateur pour voir cette page.');
                    setIsLoading(false);
                    return;
                }

                // Récupérer les annonces
                const announcementsRef = ref(database, 'announcements');
                const snapshot = await get(announcementsRef);

                if (!snapshot.exists()) {
                    setAnnouncements([]);
                    setFilteredAnnouncements([]);
                    setIsLoading(false);
                    return;
                }

                const announcementsData = snapshot.val();
                const processedAnnouncements = Object.entries(announcementsData).map(([id, data]) => ({
                    id,
                    ...data
                }));

                // Trier par date de publication (plus récent en premier)
                processedAnnouncements.sort((a, b) => {
                    const dateA = a.publishedAt ? new Date(a.publishedAt) : new Date(0);
                    const dateB = b.publishedAt ? new Date(b.publishedAt) : new Date(0);
                    return dateB - dateA;
                });

                setAnnouncements(processedAnnouncements);
                setFilteredAnnouncements(processedAnnouncements);

            } catch (error) {
                console.error("Erreur lors de la récupération des annonces:", error);
                setError("Une erreur s'est produite lors du chargement des annonces.");
            } finally {
                setIsLoading(false);
            }
        }

        fetchData();
    }, [userRole]);

    // Appliquer les filtres
    useEffect(() => {
        let result = [...announcements];

        // Appliquer la recherche
        if (searchQuery) {
            result = result.filter(
                announcement =>
                    announcement.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    announcement.content?.toLowerCase().includes(searchQuery.toLowerCase())
            );
        }

        // Appliquer le filtre d'importance
        if (importanceFilter !== 'all') {
            result = result.filter(announcement => announcement.importance === importanceFilter);
        }

        // Appliquer le filtre de statut
        if (statusFilter !== 'all') {
            const isPublished = statusFilter === 'published';
            result = result.filter(announcement => announcement.published === isPublished);
        }

        // Appliquer le filtre d'audience
        if (audienceFilter !== 'all') {
            result = result.filter(announcement => announcement.targetAudience === audienceFilter);
        }

        setFilteredAnnouncements(result);
    }, [announcements, searchQuery, importanceFilter, statusFilter, audienceFilter]);

    // Gérer le changement dans le formulaire
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    // Ouvrir le formulaire en mode création
    const openCreateForm = () => {
        const today = new Date();
        const nextMonth = new Date(today);
        nextMonth.setMonth(nextMonth.getMonth() + 1);

        setFormData({
            title: '',
            content: '',
            importance: 'normal',
            published: false,
            expiresAt: nextMonth.toISOString().split('T')[0],
            targetAudience: 'all'
        });
        setFormMode('create');
        setIsFormOpen(true);
    };

    // Ouvrir le formulaire en mode édition
    const openEditForm = (announcement) => {
        setCurrentAnnouncement(announcement);

        // Formater la date d'expiration pour l'input date
        let expiresAt = '';
        if (announcement.expiresAt) {
            const date = new Date(announcement.expiresAt);
            expiresAt = date.toISOString().split('T')[0];
        }

        setFormData({
            title: announcement.title || '',
            content: announcement.content || '',
            importance: announcement.importance || 'normal',
            published: announcement.published || false,
            expiresAt: expiresAt,
            targetAudience: announcement.targetAudience || 'all'
        });
        setFormMode('edit');
        setIsFormOpen(true);
    };

    // Créer une nouvelle annonce
    const createAnnouncement = async () => {
        try {
            const newAnnouncement = {
                ...formData,
                createdAt: new Date().toISOString(),
                publishedAt: formData.published ? new Date().toISOString() : null,
                expiresAt: formData.expiresAt ? new Date(formData.expiresAt).toISOString() : null
            };

            const announcementsRef = ref(database, 'announcements');
            const newAnnouncementRef = push(announcementsRef);
            await set(newAnnouncementRef, newAnnouncement);

            // Ajouter la nouvelle annonce à la liste locale
            setAnnouncements(prev => [{
                id: newAnnouncementRef.key,
                ...newAnnouncement
            }, ...prev]);

            setIsFormOpen(false);
            alert("Annonce créée avec succès!");
        } catch (error) {
            console.error("Erreur lors de la création de l'annonce:", error);
            alert(`Erreur: ${error.message}`);
        }
    };

    // Mettre à jour une annonce existante
    const updateAnnouncement = async () => {
        if (!currentAnnouncement) return;

        try {
            const announcementRef = ref(database, `announcements/${currentAnnouncement.id}`);

            // Vérifier si le statut de publication a changé
            const publishedChanged = currentAnnouncement.published !== formData.published;

            const updatedAnnouncement = {
                ...formData,
                updatedAt: new Date().toISOString(),
                // Mettre à jour publishedAt uniquement si on passe de non publié à publié
                publishedAt: publishedChanged && formData.published ? new Date().toISOString() : currentAnnouncement.publishedAt,
                expiresAt: formData.expiresAt ? new Date(formData.expiresAt).toISOString() : null
            };

            await update(announcementRef, updatedAnnouncement);

            // Mettre à jour l'annonce dans la liste locale
            setAnnouncements(prev =>
                prev.map(a =>
                    a.id === currentAnnouncement.id ? { ...a, ...updatedAnnouncement } : a
                )
            );

            setIsFormOpen(false);
            alert("Annonce mise à jour avec succès!");
        } catch (error) {
            console.error("Erreur lors de la mise à jour de l'annonce:", error);
            alert(`Erreur: ${error.message}`);
        }
    };

    // Confirmer la suppression d'une annonce
    const confirmDelete = (announcement) => {
        setCurrentAnnouncement(announcement);
        setIsDeleteDialogOpen(true);
    };

    // Supprimer une annonce
    const deleteAnnouncement = async () => {
        if (!currentAnnouncement) return;

        try {
            const announcementRef = ref(database, `announcements/${currentAnnouncement.id}`);
            await remove(announcementRef);

            // Supprimer l'annonce de la liste locale
            setAnnouncements(prev => prev.filter(a => a.id !== currentAnnouncement.id));

            setIsDeleteDialogOpen(false);
            alert("Annonce supprimée avec succès!");
        } catch (error) {
            console.error("Erreur lors de la suppression de l'annonce:", error);
            alert(`Erreur: ${error.message}`);
        }
    };

    // Basculer le statut de publication d'une annonce
    const togglePublishStatus = async (announcement) => {
        try {
            const announcementRef = ref(database, `announcements/${announcement.id}`);
            const newPublishedStatus = !announcement.published;

            await update(announcementRef, {
                published: newPublishedStatus,
                publishedAt: newPublishedStatus ? new Date().toISOString() : null,
                updatedAt: new Date().toISOString()
            });

            // Mettre à jour l'annonce dans la liste locale
            setAnnouncements(prev =>
                prev.map(a =>
                    a.id === announcement.id
                        ? {
                            ...a,
                            published: newPublishedStatus,
                            publishedAt: newPublishedStatus ? new Date().toISOString() : null,
                            updatedAt: new Date().toISOString()
                        }
                        : a
                )
            );

        } catch (error) {
            console.error("Erreur lors de la mise à jour du statut de publication:", error);
            alert(`Erreur: ${error.message}`);
        }
    };

    // Soumission du formulaire
    const handleSubmit = (e) => {
        e.preventDefault();

        // Validation de base
        if (!formData.title || !formData.content) {
            alert("Veuillez remplir tous les champs obligatoires (titre, contenu).");
            return;
        }

        if (formMode === 'create') {
            createAnnouncement();
        } else {
            updateAnnouncement();
        }
    };

    // Formatage de la date
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';

        return new Date(dateString).toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Vérifier si une annonce est expirée
    const isExpired = (announcement) => {
        if (!announcement.expiresAt) return false;

        const expiryDate = new Date(announcement.expiresAt);
        return expiryDate < new Date();
    };

    // Réinitialiser les filtres
    const resetFilters = () => {
        setSearchQuery('');
        setImportanceFilter('all');
        setStatusFilter('all');
        setAudienceFilter('all');
    };

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-indigo-100">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-indigo-100">
                <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
                    <div className="text-center">
                        <svg className="mx-auto h-12 w-12 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <h3 className="mt-2 text-lg font-medium text-gray-900">Erreur</h3>
                        <p className="mt-1 text-sm text-gray-500">{error}</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100">
            <Header title="Gestion des Annonces" />

            <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Gestion des Annonces</h1>
                        <p className="text-gray-500">Créez et gérez les annonces visibles par les utilisateurs</p>
                    </div>

                    <button
                        type="button"
                        onClick={openCreateForm}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                        <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Créer une annonce
                    </button>
                </div>

                {/* Filtres */}
                <div className="bg-white shadow rounded-lg mb-6">
                    <div className="px-4 py-5 sm:p-6">
                        <h2 className="text-lg font-medium text-gray-900 mb-4">Filtrer les annonces</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            <div>
                                <label htmlFor="search" className="block text-sm font-medium text-gray-700">Recherche</label>
                                <div className="mt-1 relative rounded-md shadow-sm">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                        </svg>
                                    </div>
                                    <input
                                        type="text"
                                        name="search"
                                        id="search"
                                        className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-3 py-2 border-gray-300 rounded-md"
                                        placeholder="Titre, contenu..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                    />
                                </div>
                            </div>

                            <div>
                                <label htmlFor="importance" className="block text-sm font-medium text-gray-700">Importance</label>
                                <select
                                    id="importance"
                                    name="importance"
                                    className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                                    value={importanceFilter}
                                    onChange={(e) => setImportanceFilter(e.target.value)}
                                >
                                    <option value="all">Toutes les importances</option>
                                    <option value="normal">Normale</option>
                                    <option value="important">Importante</option>
                                </select>
                            </div>

                            <div>
                                <label htmlFor="status" className="block text-sm font-medium text-gray-700">Statut</label>
                                <select
                                    id="status"
                                    name="status"
                                    className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                >
                                    <option value="all">Tous les statuts</option>
                                    <option value="published">Publiées</option>
                                    <option value="draft">Brouillons</option>
                                </select>
                            </div>

                            <div>
                                <label htmlFor="audience" className="block text-sm font-medium text-gray-700">Audience</label>
                                <select
                                    id="audience"
                                    name="audience"
                                    className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                                    value={audienceFilter}
                                    onChange={(e) => setAudienceFilter(e.target.value)}
                                >
                                    <option value="all">Toutes les audiences</option>
                                    <option value="all">Tout le monde</option>
                                    <option value="players">Joueurs</option>
                                    <option value="admins">Administrateurs</option>
                                </select>
                            </div>
                        </div>
                    </div>
                    <div className="px-4 py-3 bg-gray-50 text-right sm:px-6 flex justify-between items-center">
                        <div className="text-sm text-gray-500">
                            {filteredAnnouncements.length} annonce(s) trouvée(s)
                        </div>
                        <button
                            type="button"
                            onClick={resetFilters}
                            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                            Réinitialiser les filtres
                        </button>
                    </div>
                </div>

                {/* Liste des annonces */}
                {filteredAnnouncements.length === 0 ? (
                    <div className="bg-white shadow rounded-lg p-6 text-center">
                        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                        </svg>
                        <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune annonce</h3>
                        <p className="mt-1 text-sm text-gray-500">
                            Commencez par créer une nouvelle annonce.
                        </p>
                        <div className="mt-6">
                            <button
                                type="button"
                                onClick={openCreateForm}
                                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            >
                                <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                </svg>
                                Créer une annonce
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-6">
                        {filteredAnnouncements.map((announcement) => (
                            <div
                                key={announcement.id}
                                className={`bg-white shadow rounded-lg overflow-hidden ${
                                    isExpired(announcement) ? 'opacity-60' : ''
                                }`}
                            >
                                <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
                                    <div>
                                        <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center">
                                            {announcement.title}
                                            {announcement.importance === 'important' && (
                                                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                                    Important
                                                </span>
                                            )}
                                            {isExpired(announcement) && (
                                                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                                    Expirée
                                                </span>
                                            )}
                                        </h3>
                                        <p className="mt-1 max-w-2xl text-sm text-gray-500">
                                            {announcement.published ? (
                                                <span className="inline-flex items-center">
                                                    <span className="h-2 w-2 rounded-full bg-green-400 mr-1.5"></span>
                                                    Publiée le {formatDate(announcement.publishedAt)}
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center">
                                                    <span className="h-2 w-2 rounded-full bg-gray-400 mr-1.5"></span>
                                                    Brouillon
                                                </span>
                                            )}
                                            {announcement.expiresAt && (
                                                <span className="ml-4">
                                                    Expire le {formatDate(announcement.expiresAt)}
                                                </span>
                                            )}
                                        </p>
                                    </div>
                                    <div className="flex space-x-2">
                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                            announcement.targetAudience === 'all'
                                                ? 'bg-blue-100 text-blue-800'
                                                : announcement.targetAudience === 'players'
                                                    ? 'bg-green-100 text-green-800'
                                                    : 'bg-purple-100 text-purple-800'
                                        }`}>
                                            {announcement.targetAudience === 'all'
                                                ? 'Tous'
                                                : announcement.targetAudience === 'players'
                                                    ? 'Joueurs'
                                                    : 'Admins'
                                            }
                                        </span>
                                    </div>
                                </div>
                                <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
                                    <p className="text-sm text-gray-900 whitespace-pre-line">
                                        {announcement.content}
                                    </p>
                                </div>
                                <div className="border-t border-gray-200 px-4 py-4 sm:px-6 bg-gray-50 flex justify-end space-x-3">
                                    <button
                                        type="button"
                                        onClick={() => openEditForm(announcement)}
                                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                    >
                                        <svg className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                        </svg>
                                        Modifier
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => togglePublishStatus(announcement)}
                                        className={`inline-flex items-center px-3 py-1.5 border shadow-sm text-sm leading-4 font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                                            announcement.published
                                                ? 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50 focus:ring-indigo-500'
                                                : 'border-transparent text-white bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500'
                                        }`}
                                    >
                                        {announcement.published ? (
                                            <>
                                                <svg className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                                                </svg>
                                                Dépublier
                                            </>
                                        ) : (
                                            <>
                                                <svg className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                                                </svg>
                                                Publier
                                            </>
                                        )}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => confirmDelete(announcement)}
                                        className="inline-flex items-center px-3 py-1.5 border border-transparent shadow-sm text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                    >
                                        <svg className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                        Supprimer
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </main>

            {/* Modal de création/édition d'annonce */}
            {isFormOpen && (
                <div className="fixed z-10 inset-0 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
                    <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>

                        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
                            <form onSubmit={handleSubmit}>
                                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                    <div className="sm:flex sm:items-start">
                                        <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                                            <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                                                {formMode === 'create' ? 'Créer une nouvelle annonce' : 'Modifier l\'annonce'}
                                            </h3>
                                            <div className="mt-2">
                                                <p className="text-sm text-gray-500">
                                                    Remplissez tous les champs obligatoires pour {formMode === 'create' ? 'créer' : 'mettre à jour'} l'annonce.
                                                </p>
                                            </div>

                                            <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                                                <div className="sm:col-span-6">
                                                    <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                                                        Titre <span className="text-red-500">*</span>
                                                    </label>
                                                    <div className="mt-1">
                                                        <input
                                                            type="text"
                                                            name="title"
                                                            id="title"
                                                            className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                                            placeholder="Titre de l'annonce"
                                                            value={formData.title}
                                                            onChange={handleInputChange}
                                                            required
                                                        />
                                                    </div>
                                                </div>

                                                <div className="sm:col-span-6">
                                                    <label htmlFor="content" className="block text-sm font-medium text-gray-700">
                                                        Contenu <span className="text-red-500">*</span>
                                                    </label>
                                                    <div className="mt-1">
                                                        <textarea
                                                            id="content"
                                                            name="content"
                                                            rows={6}
                                                            className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                                            placeholder="Contenu de l'annonce..."
                                                            value={formData.content}
                                                            onChange={handleInputChange}
                                                            required
                                                        />
                                                    </div>
                                                </div>

                                                <div className="sm:col-span-3">
                                                    <label htmlFor="importance" className="block text-sm font-medium text-gray-700">
                                                        Importance
                                                    </label>
                                                    <div className="mt-1">
                                                        <select
                                                            id="importance"
                                                            name="importance"
                                                            className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                                            value={formData.importance}
                                                            onChange={handleInputChange}
                                                        >
                                                            <option value="normal">Normale</option>
                                                            <option value="important">Importante</option>
                                                        </select>
                                                    </div>
                                                </div>

                                                <div className="sm:col-span-3">
                                                    <label htmlFor="targetAudience" className="block text-sm font-medium text-gray-700">
                                                        Audience cible
                                                    </label>
                                                    <div className="mt-1">
                                                        <select
                                                            id="targetAudience"
                                                            name="targetAudience"
                                                            className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                                            value={formData.targetAudience}
                                                            onChange={handleInputChange}
                                                        >
                                                            <option value="all">Tous les utilisateurs</option>
                                                            <option value="players">Joueurs uniquement</option>
                                                            <option value="admins">Administrateurs uniquement</option>
                                                        </select>
                                                    </div>
                                                </div>

                                                <div className="sm:col-span-3">
                                                    <label htmlFor="expiresAt" className="block text-sm font-medium text-gray-700">
                                                        Date d'expiration
                                                    </label>
                                                    <div className="mt-1">
                                                        <input
                                                            type="date"
                                                            name="expiresAt"
                                                            id="expiresAt"
                                                            className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                                            value={formData.expiresAt}
                                                            onChange={handleInputChange}
                                                        />
                                                    </div>
                                                </div>

                                                <div className="sm:col-span-3 flex items-center h-full mt-6">
                                                    <div className="flex items-center">
                                                        <input
                                                            id="published"
                                                            name="published"
                                                            type="checkbox"
                                                            checked={formData.published}
                                                            onChange={handleInputChange}
                                                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                                                        />
                                                        <label htmlFor="published" className="ml-2 block text-sm text-gray-900">
                                                            Publier immédiatement
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                                    <button
                                        type="submit"
                                        className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm"
                                    >
                                        {formMode === 'create' ? 'Créer l\'annonce' : 'Mettre à jour'}
                                    </button>
                                    <button
                                        type="button"
                                        className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                                        onClick={() => setIsFormOpen(false)}
                                    >
                                        Annuler
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Dialogue de confirmation de suppression */}
            {isDeleteDialogOpen && (
                <div className="fixed z-10 inset-0 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
                    <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>

                        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                <div className="sm:flex sm:items-start">
                                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                                        <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                        </svg>
                                    </div>
                                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                        <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                                            Confirmer la suppression
                                        </h3>
                                        <div className="mt-2">
                                            <p className="text-sm text-gray-500">
                                                Êtes-vous sûr de vouloir supprimer cette annonce ? Cette action est irréversible.
                                            </p>
                                            {currentAnnouncement && (
                                                <p className="mt-2 text-sm font-medium text-gray-900">
                                                    "{currentAnnouncement.title}"
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                                <button
                                    type="button"
                                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                                    onClick={deleteAnnouncement}
                                >
                                    Supprimer
                                </button>
                                <button
                                    type="button"
                                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                                    onClick={() => setIsDeleteDialogOpen(false)}
                                >
                                    Annuler
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
